import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Btn } from '../ui/Btn.jsx';
import { FinleyInsightBanner } from '../ui/FinleyInsightBanner.jsx';
import { FUN_MODE_LABELS } from '../../lib/playlist.js';
import { getInsightMessage } from '../../config/insightMessages.js';
import { popIn, staggerChild, staggerContainer, tapBounce, tapScale } from '../../lib/motionPresets.js';

function PlaylistCard({ item, index, currentIdx, onMoveUp, onMoveDown, onSwapToggle, swapActive, showSwap, done = false }) {
  const info = FUN_MODE_LABELS[item.mode] || { emoji: '📚', label: item.mode, desc: '' };
  const isCurrent = index === currentIdx;
  return (
    <motion.div
      variants={staggerChild}
      className={`rounded-finley-lg border-2 p-4 shadow-finley-md ${
        done
          ? 'border-finley-success/25 bg-finley-success/10'
          : isCurrent
            ? 'border-finley-primary/30 bg-finley-primary/10'
            : 'border-finley-text-muted/10 bg-finley-bg-card'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-finley-full bg-finley-bg text-lg shadow-finley-sm">
          {done ? '✅' : info.emoji}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-finley-display text-lg text-finley-text">{info.label}</div>
          <div className="text-sm text-finley-text-light">{item.reason || info.desc}</div>
          <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-finley-text-light">
            {item.minutes} min
          </div>
        </div>
        {!done && (
          <div className="flex items-center gap-2">
            {typeof onMoveUp === 'function' && (
              <motion.button
                {...tapScale}
                onClick={onMoveUp}
                className="flex h-9 w-9 items-center justify-center rounded-finley-md border border-finley-text-muted/20 bg-finley-bg text-finley-text-light shadow-finley-sm"
              >
                ↑
              </motion.button>
            )}
            {typeof onMoveDown === 'function' && (
              <motion.button
                {...tapScale}
                onClick={onMoveDown}
                className="flex h-9 w-9 items-center justify-center rounded-finley-md border border-finley-text-muted/20 bg-finley-bg text-finley-text-light shadow-finley-sm"
              >
                ↓
              </motion.button>
            )}
            {showSwap && (
              <motion.button
                {...tapScale}
                onClick={onSwapToggle}
                className={`rounded-finley-md px-3 py-2 text-xs font-semibold shadow-finley-sm ${
                  swapActive
                    ? 'border-2 border-finley-secondary/35 bg-finley-secondary/10 text-finley-secondary'
                    : 'border border-finley-text-muted/20 bg-finley-bg text-finley-text-light'
                }`}
              >
                Swap
              </motion.button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function PlaylistTransition({ onDone, age, currentIdx = 0, total = 0 }) {
  const messages = (age || 7) <= 6
    ? ['Great job!', 'You rock!', 'Woohoo!', 'Amazing!']
    : ['Nice work! Ready for the next one?', 'Great job! Something new next!', 'Awesome! Let\'s keep going!'];
  const [msg] = useState(() => messages[Math.floor(Math.random() * messages.length)]);
  const [insightMessage, setInsightMessage] = useState('');
  const [insightVisible, setInsightVisible] = useState(false);
  const [countdown, setCountdown] = useState(3);
  useEffect(() => {
    const insight = getInsightMessage({
      activityKey: `playlist-transition:${currentIdx}`,
      trigger: currentIdx > 0 ? 'difficulty_shift' : 'session_start',
      force: currentIdx === 0
    });
    if (insight) {
      setInsightMessage(insight);
      setInsightVisible(true);
    }
  }, [currentIdx]);
  useEffect(() => {
    const iv = setInterval(() => {
      setCountdown((value) => {
        if (value <= 1) {
          clearInterval(iv);
          onDone();
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => {
      clearInterval(iv);
    };
  }, [onDone]);
  return (
    <div className="relative flex h-full flex-col items-center justify-center px-6 py-10 text-center">
      <FinleyInsightBanner message={insightMessage} visible={insightVisible} />
      <motion.div {...popIn} className="mb-4 text-7xl">🐬</motion.div>
      <div className="mb-2 font-finley-display text-3xl text-finley-primary">{msg}</div>
      {total > 0 && <div className="text-sm font-semibold text-finley-text-light">Activity {Math.min(currentIdx + 1, total)} of {total}</div>}
      <div className="mt-2 text-xs font-semibold uppercase tracking-[0.12em] text-finley-text-light">Starting in {countdown}</div>
      <motion.button {...tapBounce} onClick={onDone} className="mt-5 min-h-[52px] rounded-finley-lg bg-finley-primary px-6 py-3 font-finley-display text-base text-white shadow-finley-md">
        Next Activity
      </motion.button>
    </div>
  );
}

export function PlaylistStart({ name, playlist = [], onStart }) {
  return (
    <div className="px-6 py-8 text-center">
      <motion.div {...popIn} className="mb-4 text-7xl">🐬</motion.div>
      <h2 className="mb-2 font-finley-display text-3xl text-finley-primary">Session Ready</h2>
      <p className="mb-6 text-base text-finley-text-light">Finley picked {playlist.length || 1} activities for {name} today.</p>
      <Btn onClick={onStart} color="var(--color-finley-primary)" full>Let&apos;s Go! 🚀</Btn>
    </div>
  );
}

export function PlaylistChoice({ options, name, onPick, currentIdx = 0, total = 0 }) {
  return (
    <div className="px-6 py-6">
      <div className="mb-2 text-center font-finley-display text-3xl text-finley-primary">Choose the next adventure</div>
      <p className="mb-6 text-center text-sm text-finley-text-light">{name}, Finley recommends one of these next. Activity {currentIdx + 1} of {total}.</p>
      <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-4">
        {options.map((opt, i) => {
          const info = FUN_MODE_LABELS[opt.mode] || { emoji: '📚', label: opt.mode, desc: '' };
          return (
            <motion.button
              key={`${opt.mode}-${i}`}
              variants={staggerChild}
              {...tapBounce}
              onClick={() => onPick(opt)}
              className={`w-full rounded-finley-lg border-2 p-5 text-left shadow-finley-md ${
                i === 0 ? 'border-finley-primary/30 bg-finley-primary/10' : 'border-finley-text-muted/10 bg-finley-bg-card'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="text-4xl">{info.emoji}</div>
                <div className="flex-1">
                  <div className="font-finley-display text-xl text-finley-text">{info.label}</div>
                  <div className="text-sm text-finley-text-light">{info.desc}</div>
                </div>
                {i === 0 && <div className="rounded-finley-full bg-finley-gold px-3 py-1 text-xs font-semibold text-finley-text shadow-finley-sm">Finley&apos;s pick</div>}
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
}

export function PlaylistPlan({ playlist, name, currentIdx = 0, onStart, onReorder, onSwap, swapsLeft, bubbleUnlocked, coachingPromptsEnabled = true }) {
  const allModes = ['phonics', 'sight', 'stories', ...(bubbleUnlocked ? ['bubbles'] : [])];
  const [swapIdx, setSwapIdx] = useState(null);

  return (
    <div className="px-6 py-6">
      <div className="mb-2 text-center font-finley-display text-3xl text-finley-primary">Today&apos;s Reading Plan</div>
      <p className="mb-6 text-center text-sm text-finley-text-light">Finley built this session for {name}. Start at the top and we&apos;ll guide each activity in order.</p>
      <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-3">
        {playlist.map((item, i) => (
          <PlaylistCard
            key={`${item.mode}-${i}`}
            item={item}
            index={i}
            currentIdx={currentIdx}
            onMoveUp={i > 0 ? () => onReorder(i, i - 1) : null}
            onMoveDown={i < playlist.length - 1 ? () => onReorder(i, i + 1) : null}
            onSwapToggle={swapsLeft > 0 ? () => setSwapIdx((cur) => cur === i ? null : i) : null}
            swapActive={swapIdx === i}
            showSwap={swapsLeft > 0}
          />
        ))}
      </motion.div>
      {swapIdx !== null && (
        <div className="mt-4 rounded-finley-lg border-2 border-finley-secondary/20 bg-finley-secondary/10 p-4 shadow-finley-sm">
          <div className="mb-3 font-finley-display text-sm text-finley-secondary">Swap this activity with</div>
          <div className="flex flex-wrap gap-2">
            {allModes.filter((mode) => mode !== playlist[swapIdx].mode).map((mode) => {
              const info = FUN_MODE_LABELS[mode];
              return (
                <motion.button
                  key={mode}
                  {...tapScale}
                  onClick={() => {
                    onSwap(swapIdx, mode);
                    setSwapIdx(null);
                  }}
                  className="rounded-finley-md border border-finley-text-muted/20 bg-finley-bg px-3 py-2 text-sm text-finley-text shadow-finley-sm"
                >
                  {info.emoji} {info.label}
                </motion.button>
              );
            })}
          </div>
        </div>
      )}
      <div className="mt-4 text-center text-xs font-semibold uppercase tracking-[0.12em] text-finley-text-light">
        {swapsLeft > 0 ? `${swapsLeft} free play swap remaining` : 'Playlist locked in'}
      </div>
      {coachingPromptsEnabled && (
        <div
          className="mt-4 flex items-start gap-2 rounded-finley-md px-3 py-3 text-[13px] leading-5"
          style={{
            background: 'rgba(13,148,136,0.06)',
            border: '1px solid rgba(13,148,136,0.15)',
            color: 'rgba(0,0,0,0.62)'
          }}
        >
          <span>👋</span>
          <span>Want to boost results? Join this session - just watch and cheer. Research shows parent presence increases reading gains.</span>
        </div>
      )}
      <motion.button
        {...tapBounce}
        onClick={onStart}
        className="mt-5 min-h-[56px] w-full rounded-finley-lg bg-finley-primary px-6 py-4 font-finley-display text-lg text-white shadow-finley-md"
      >
        Let&apos;s Go!
      </motion.button>
    </div>
  );
}

export function PlaylistProgress({ playlist, currentIdx, onContinue }) {
  return (
    <div className="px-6 py-6">
      <div className="mb-2 text-center font-finley-display text-3xl text-finley-primary">Session Progress</div>
      <p className="mb-6 text-center text-sm text-finley-text-light">Activity {Math.min(currentIdx + 1, playlist.length)} of {playlist.length}</p>
      <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-3">
        {playlist.map((item, i) => (
          <PlaylistCard
            key={`${item.mode}-${i}`}
            item={item}
            index={i}
            currentIdx={currentIdx}
            done={i < currentIdx}
          />
        ))}
      </motion.div>
      <Btn onClick={onContinue} color="var(--color-finley-primary)" full>Continue →</Btn>
    </div>
  );
}
